import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Modal } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { usePopup } from '../../lib/popup';

import DeleteStep from '../DeleteStep';
import styles from './MonthlyReportModal.module.scss';
import { formatPoints } from '../../utils/formatPoints';
import Paths from '../../constants/Paths';

const MonthTitles = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const buildMonthOptions = () =>
  MonthTitles.map((text, idx) => ({
    key: idx + 1,
    text,
    value: idx + 1,
  }));

const buildYearOptions = (years) =>
  (years || []).map((y) => ({
    key: y,
    text: String(y),
    value: y,
  }));

const formatDate = (value) => {
  if (!value) {
    return '';
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }

  return d.toLocaleDateString('ru-RU');
};

const StatRow = React.memo(({ label, value, labelClassName }) => (
  <div className={styles.statRow}>
    <div className={`${styles.statLabel} ${labelClassName || ''}`}>{label}</div>
    <div className={styles.statValue}>{value}</div>
  </div>
));

StatRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  labelClassName: PropTypes.string,
};

const CategorySummaryCard = React.memo(({ title, count, points, views }) => (
  <div className={styles.categoryCard}>
    <div className={styles.categoryTitle}>{title}</div>
    <div className={styles.categoryStats}>
      <StatRow label="Количество" value={count} />
      <StatRow label="Баллы" value={formatPoints(points)} />
      <StatRow label="Просмотры" value={views} />
    </div>
  </div>
));

CategorySummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  views: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const TaskBlock = React.memo(({ task }) => {
  const coefficientNum = Number(task.coefficient);
  const isCoefficientFinite = Number.isFinite(coefficientNum);
  const coefficientText = isCoefficientFinite ? coefficientNum.toFixed(2) : task.coefficient;

  const coefficientClassName = isCoefficientFinite
    ? `${styles.coefficientValue} ${coefficientNum < 1 ? styles.coefficientBad : styles.coefficientGood}`
    : null;

  return (
    <div className={styles.taskBlock}>
      <div className={styles.taskHeader}>
        <div className={styles.taskHeaderLeft}>
          <div className={styles.taskNumber}>{`Задача №${task.number}`}</div>
          <div className={styles.taskNameLabel}>Название</div>
          <div className={styles.taskName}>{task.name}</div>
        </div>
        <div className={styles.taskHeaderRight}>
          <div className={styles.taskDates}>
            <StatRow label="Дата создания" value={task.createdAt} />
            <StatRow label="Дата завершения" value={task.closedAt} />
          </div>
        </div>
      </div>

      <div className={styles.taskGrid}>
        <div className={styles.taskRow}>
          <div className={styles.taskRowTitle}>Категория</div>
          <div className={styles.taskRowValue}>{task.category}</div>
        </div>
        <div className={styles.taskRow}>
          <div className={styles.taskRowTitle}>ТИП</div>
          <div className={styles.taskRowValue}>{task.type}</div>
        </div>
        <div className={styles.taskRow}>
          <div className={styles.taskRowTitle}>Основные баллы</div>
          <div className={styles.taskRowValue}>{formatPoints(task.basePoints)}</div>
        </div>
        <div className={styles.taskRow}>
          <div className={styles.taskRowTitle}>Дополнительные баллы</div>
          <div className={styles.taskRowValue}>
            {Array.isArray(task.extraPoints) && task.extraPoints.length > 0 ? (
              <div className={styles.extraPointsList}>
                {task.extraPoints.map((p, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`${task.number}-${idx}`} className={styles.extraPointsItem}>
                    <span className={styles.extraPointsValue}>{`+${formatPoints(p.value)}`}</span>
                    <span>{p.description || ''}</span>
                  </div>
                ))}
              </div>
            ) : (
              '0'
            )}
          </div>
        </div>
        <div className={styles.taskRow}>
          <div className={styles.taskRowTitle}>Просмотры</div>
          <div className={styles.taskRowValue}>{task.views}</div>
        </div>
        <div className={styles.taskRow}>
          <div className={styles.taskRowTitle}>Коэффициент</div>
          <div className={styles.taskRowValue}>
            {coefficientClassName ? <span className={coefficientClassName}>{coefficientText}</span> : coefficientText}
          </div>
        </div>
        <div className={styles.taskRow}>
          <div className={styles.taskRowTitle}>Итоговые баллы за задачу после расчета с коэффициентом</div>
          <div className={styles.taskRowValue}>{formatPoints(task.totalPoints)}</div>
        </div>
      </div>
    </div>
  );
});

TaskBlock.propTypes = {
  task: PropTypes.shape({
    number: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    closedAt: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    basePoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    extraPoints: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        description: PropTypes.string,
      }),
    ).isRequired,
    views: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    coefficient: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    totalPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    comment: PropTypes.string.isRequired,
  }).isRequired,
};

const TaskBadge = React.memo(({ task }) => {
  const badgeContent = (
    <>
      <span className={styles.listName}>{task.listName}</span>
      <span className={styles.arrow}>→</span>
      <span className={styles.taskName}>{task.name}</span>
    </>
  );

  if (task.cardId) {
    return (
      <Link to={Paths.CARDS.replace(':id', task.cardId)} className={styles.taskBadge}>
        {badgeContent}
      </Link>
    );
  }

  return (
    <span className={styles.taskBadge}>
      {badgeContent}
    </span>
  );
});

TaskBadge.propTypes = {
  task: PropTypes.shape({
    name: PropTypes.string.isRequired,
    listName: PropTypes.string.isRequired,
    cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

const TaskNameOnly = React.memo(({ task }) => {
  if (task.cardId) {
    return (
      <Link to={Paths.CARDS.replace(':id', task.cardId)} className={styles.taskBadge}>
        <span className={styles.taskName}>{task.name}</span>
      </Link>
    );
  }

  return (
    <span className={styles.taskBadge}>
      <span className={styles.taskName}>{task.name}</span>
    </span>
  );
});

TaskNameOnly.propTypes = {
  task: PropTypes.shape({
    name: PropTypes.string.isRequired,
    cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

const MonthlyReportModal = React.memo(
  ({ open, pointSystem, listId, report, isReportLoading, onReportFetch, onClose, unclosedTasks, allUnclosedTasksInList, onCompleteMonth, contentViewPlans, onContentViewPlansFetch, onEmployeePlansFetch, onDampingCoefficientsFetch, onOpenUnclosedTasksModal, onListReportDataFetch, onListReportDataUpsert, listReportData, employeePlans }) => {
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  
  const DeletePopup = usePopup(DeleteStep);

  // Загружаем ContentViewPlan, EmployeePlan, ListReportData и DampingCoefficients при открытии модалки
  useEffect(() => {
    if (open && pointSystem) {
      onContentViewPlansFetch();
      if (onEmployeePlansFetch) {
        onEmployeePlansFetch();
      }
      if (onDampingCoefficientsFetch) {
        onDampingCoefficientsFetch();
      }
      if (listId && onListReportDataFetch) {
        onListReportDataFetch();
      }
    }
  }, [open, pointSystem, listId, onContentViewPlansFetch, onEmployeePlansFetch, onDampingCoefficientsFetch, onListReportDataFetch]);

  // Получаем уникальные года из заполненных ContentViewPlan
  const availableYears = useMemo(() => {
    if (!contentViewPlans || contentViewPlans.length === 0) {
      return [];
    }
    const yearsSet = new Set(
      contentViewPlans
        .map((plan) => {
          const yearValue = plan.year;
          // Пробуем разные способы получения года
          if (typeof yearValue === 'number') {
            return yearValue;
          }
          if (typeof yearValue === 'string') {
            const num = parseInt(yearValue, 10);
            return !Number.isNaN(num) ? num : null;
          }
          return null;
        })
        .filter((y) => y !== null && y > 0)
    );
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [contentViewPlans]);

  // Получаем доступные месяцы для выбранного года
  const availableMonths = useMemo(() => {
    if (!contentViewPlans || contentViewPlans.length === 0 || !year) {
      return [];
    }
    // Приводим year к числу для корректного сравнения
    const yearNum = typeof year === 'number' ? year : parseInt(String(year), 10);
    if (Number.isNaN(yearNum)) {
      return [];
    }
    
    const filteredPlans = contentViewPlans.filter((plan) => {
      const planYear = typeof plan.year === 'number' 
        ? plan.year 
        : parseInt(String(plan.year), 10);
      const planMonth = typeof plan.month === 'number'
        ? plan.month
        : parseInt(String(plan.month), 10);
      
      return !Number.isNaN(planYear) && 
             !Number.isNaN(planMonth) && 
             planYear === yearNum && 
             planMonth >= 1 && 
             planMonth <= 12;
    });
    
    const monthsSet = new Set(
      filteredPlans
        .map((plan) => {
          const planMonth = typeof plan.month === 'number'
            ? plan.month
            : parseInt(String(plan.month), 10);
          return planMonth;
        })
        .filter((m) => !Number.isNaN(m) && m >= 1 && m <= 12)
    );
    return Array.from(monthsSet).sort((a, b) => a - b);
  }, [contentViewPlans, year]);

  // Формируем опции для годов - только заполненные
  const yearOptions = useMemo(() => {
    const yearsToUse = availableYears.length > 0 ? availableYears : (report?.years && Array.isArray(report.years) ? report.years : []);
    return buildYearOptions(yearsToUse);
  }, [availableYears, report]);

  // Формируем опции для месяцев - только заполненные для выбранного года
  const monthOptions = useMemo(() => {
    // Показываем только заполненные месяцы для выбранного года
    return availableMonths.map((monthValue) => ({
      key: monthValue,
      text:
        monthValue === month
          ? `План расчета: ${MonthTitles[monthValue - 1] || `Месяц ${monthValue}`}`
          : MonthTitles[monthValue - 1] || `Месяц ${monthValue}`,
      value: monthValue,
    }));
  }, [availableMonths, month]);

  // Сбрасываем значения при закрытии модалки
  useEffect(() => {
    if (!open) {
      setYear(null);
      setMonth(null);
    }
  }, [open]);

  // Устанавливаем значения из ListReportData при загрузке (только если модалка открыта и значения еще не установлены)
  useEffect(() => {
    if (open && listReportData && listReportData.year && listReportData.month && !year && !month) {
      setYear(listReportData.year);
      setMonth(listReportData.month);
    }
  }, [open, listReportData, year, month]);

  // Сбрасываем месяц при изменении года, если выбранный месяц недоступен для нового года
  useEffect(() => {
    if (!year || !month) {
      return;
    }
    if (availableMonths.length > 0 && !availableMonths.includes(month)) {
      setMonth(null);
    }
  }, [year, availableMonths, month]);

  useEffect(() => {
    if (!open || !pointSystem || !listId || !year || !month) {
      return;
    }

    onReportFetch(year, month, listId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pointSystem?.id, year, month, listId]);

  // Преобразуем незакрытые задачи в формат detailedTasks
  const unclosedTasksFormatted = useMemo(() => {
    if (!unclosedTasks || unclosedTasks.length === 0 || !year || !month) {
      return [];
    }

    const TYPE_TITLES = {
      photo: 'ФОТО',
      video: 'ВИДЕО',
      case: 'КЕЙСЫ',
    };

    // Дополнительная фильтрация по выбранному году и месяцу (на случай рассинхронизации)
    const filteredTasks = unclosedTasks.filter((task) => {
      const cardPointData = task.cardPointData;
      const createdAt = cardPointData?.createdAt || task.card?.createdAt;
      
      if (!createdAt) {
        return false;
      }
      
      const taskDate = new Date(createdAt);
      const taskYear = taskDate.getFullYear();
      const taskMonth = taskDate.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12
      
      return taskYear === year && taskMonth === month;
    });

    return filteredTasks.map((task) => {
      const contentCardDefault = task.contentCardDefault;
      const cardPointData = task.cardPointData;
      const cardPointAdvancedData = task.cardPointAdvancedData || [];

      const type = contentCardDefault?.type || '';
      const category = TYPE_TITLES[type] || '';
      const typeTitle = contentCardDefault?.name || '';
      const basePoints = Number(contentCardDefault?.value) || 0;
      const views = Number(cardPointData?.views) || 0;
      const coefficient = 1; // Для незакрытых задач коэффициент = 1

      // Преобразуем CardPointAdvancedData в формат extraPoints
      const extraPoints = (cardPointAdvancedData || []).map((item) => ({
        value: Number(item.value) || 0,
        description: item.description || '',
      }));

      const extraPointsSum = extraPoints.reduce((sum, p) => sum + (Number(p?.value) || 0), 0);
      const totalPoints = (basePoints + extraPointsSum) * coefficient;

      const createdAt = cardPointData?.createdAt || task.card?.createdAt;

      return {
        number: 0, // Будет установлен позже при объединении
        name: task.cardName || '',
        createdAt: formatDate(createdAt),
        closedAt: '', // Незакрытые задачи не имеют даты закрытия
        category,
        type: typeTitle,
        basePoints,
        extraPoints,
        views,
        coefficient,
        totalPoints,
        comment: '',
        isUnclosed: true, // Флаг для идентификации незакрытых задач
      };
    });
  }, [unclosedTasks, year, month]);

  const summaryByCategory = useMemo(() => {
    const fallback = [
      { title: 'ФОТО', count: 0, points: 0, views: 0 },
      { title: 'ВИДЕО', count: 0, points: 0, views: 0 },
      { title: 'КЕЙСЫ', count: 0, points: 0, views: 0 },
    ];

    const categoryMap = {
      'ФОТО': { title: 'ФОТО', count: 0, points: 0, views: 0 },
      'ВИДЕО': { title: 'ВИДЕО', count: 0, points: 0, views: 0 },
      'КЕЙСЫ': { title: 'КЕЙСЫ', count: 0, points: 0, views: 0 },
    };

    // Добавляем данные из отчета (закрытые задачи)
    if (report && Array.isArray(report.summaryByCategory)) {
      report.summaryByCategory.forEach((c) => {
        const title = c.title || '';
        if (categoryMap[title]) {
          categoryMap[title].count += Number(c.count) || 0;
          categoryMap[title].points += Number(c.points) || 0;
          categoryMap[title].views += Number(c.views) || 0;
        }
      });
    }

    // Добавляем данные из незакрытых задач
    unclosedTasksFormatted.forEach((task) => {
      const category = task.category;
      if (categoryMap[category]) {
        categoryMap[category].count += 1;
        categoryMap[category].points += task.totalPoints;
        categoryMap[category].views += task.views;
      }
    });

    return fallback.map((fb) => ({
      title: fb.title,
      count: categoryMap[fb.title]?.count || 0,
      points: categoryMap[fb.title]?.points || 0,
      views: categoryMap[fb.title]?.views || 0,
    }));
  }, [report, unclosedTasksFormatted]);

  const totals = useMemo(() => {
    const workCount = summaryByCategory.reduce((sum, c) => sum + (Number(c.count) || 0), 0);
    const views = summaryByCategory.reduce((sum, c) => sum + (Number(c.views) || 0), 0);
    
    // Базовые баллы из отчета (закрытые задачи)
    let defaultPoints = Number(report?.defaultPoints) || 0;
    let extraPoints = Number(report?.extraPoints) || 0;
    let coefficientPoints = Number(report?.coefficientPoints) || 0;

    // Добавляем баллы из незакрытых задач
    unclosedTasksFormatted.forEach((task) => {
      defaultPoints += task.basePoints;
      const extraPointsSum = task.extraPoints.reduce((sum, p) => sum + (Number(p?.value) || 0), 0);
      extraPoints += extraPointsSum;
      // Для незакрытых задач coefficient = 1, поэтому coefficientPoints = 0
    });

    const totalPoints = defaultPoints + extraPoints + coefficientPoints;

    return {
      workCount,
      views,
      totalPoints,
      defaultPoints,
      extraPoints,
      coefficientPoints,
    };
  }, [summaryByCategory, report, unclosedTasksFormatted]);

  const detailedTasks = useMemo(() => {
    const closedTasks = [];
    
    // Добавляем закрытые задачи из отчета
    if (report && Array.isArray(report.detailedTasks)) {
      closedTasks.push(...report.detailedTasks.map((t, idx) => ({
        number: Number(t.number) || idx + 1,
        name: t.name || '',
        createdAt: formatDate(t.createdAt),
        closedAt: formatDate(t.closedAt),
        category: t.category || '',
        type: t.type || '',
        basePoints: Number(t.basePoints) || 0,
        extraPoints: Array.isArray(t.extraPoints) ? t.extraPoints : [],
        views: Number(t.views) || 0,
        coefficient: Number(t.coefficient) || 0,
        totalPoints: Number(t.totalPoints) || 0,
        comment: t.comment || '',
        isUnclosed: false,
      })));
    }

    // Добавляем незакрытые задачи
    const allTasks = [...closedTasks, ...unclosedTasksFormatted];

    // Перенумеровываем все задачи
    return allTasks.map((task, idx) => ({
      ...task,
      number: idx + 1,
    }));
  }, [report, unclosedTasksFormatted]);

  // Форматируем задачи, закрытые не в выбранном месяце, но находящиеся в данном списке
  const tasksInListClosedInOtherMonth = useMemo(() => {
    if (!report || !Array.isArray(report.tasksInListClosedInOtherMonth)) {
      return [];
    }

    return report.tasksInListClosedInOtherMonth.map((t, idx) => ({
      number: Number(t.number) || idx + 1,
      name: t.name || '',
      createdAt: formatDate(t.createdAt),
      closedAt: formatDate(t.closedAt),
      category: t.category || '',
      type: t.type || '',
      basePoints: Number(t.basePoints) || 0,
      extraPoints: Array.isArray(t.extraPoints) ? t.extraPoints : [],
      views: Number(t.views) || 0,
      coefficient: Number(t.coefficient) || 0,
      totalPoints: Number(t.totalPoints) || 0,
      comment: t.comment || '',
      listId: t.listId || null,
      listName: t.listName || '',
      cardId: t.cardId || null,
    }));
  }, [report]);

  // Форматируем задачи из других списков
  const tasksFromOtherLists = useMemo(() => {
    if (!report || !Array.isArray(report.tasksFromOtherLists)) {
      return [];
    }

    return report.tasksFromOtherLists.map((t, idx) => ({
      number: Number(t.number) || idx + 1,
      name: t.name || '',
      createdAt: formatDate(t.createdAt),
      closedAt: formatDate(t.closedAt),
      category: t.category || '',
      type: t.type || '',
      basePoints: Number(t.basePoints) || 0,
      extraPoints: Array.isArray(t.extraPoints) ? t.extraPoints : [],
      views: Number(t.views) || 0,
      coefficient: Number(t.coefficient) || 0,
      totalPoints: Number(t.totalPoints) || 0,
      comment: t.comment || '',
      listId: t.listId || null,
      listName: t.listName || '',
      cardId: t.cardId || null,
    }));
  }, [report]);

  // Форматируем незакрытые задачи в текущем списке
  // Используем allUnclosedTasksInList, который содержит все задачи в списке,
  // у которых CardPointData.closed != true или нет CardPointData
  const unclosedTasksInCurrentList = useMemo(() => {
    if (!allUnclosedTasksInList || allUnclosedTasksInList.length === 0 || !listId) {
      return [];
    }

    return allUnclosedTasksInList.map((task) => ({
      name: task.cardName || '',
      cardId: task.cardId || null,
    }));
  }, [allUnclosedTasksInList, listId]);

  const headerTitle = useMemo(() => {
    if (!month || !year) {
      return 'ОТЧЕТ';
    }
    const monthTitle = MonthTitles[month - 1] ? MonthTitles[month - 1].toUpperCase() : '';
    return `ОТЧЕТ ЗА ${monthTitle} ${year}`;
  }, [month, year]);

  const isMonthClosed = useMemo(() => {
    return listReportData?.closed === true;
  }, [listReportData]);

  // Вычисляем уровень сотрудника
  const employeeLevel = useMemo(() => {
    // Если нет данных отчета, не вычисляем уровень
    if (!totals) {
      return null;
    }

    // Если есть listReportData и месяц закрыт, показываем сохраненный уровень
    if (listReportData && listReportData.closed === true) {
      return listReportData.employeeLevel !== null && listReportData.employeeLevel !== undefined
        ? listReportData.employeeLevel
        : null;
    }

    // Если месяц не закрыт, вычисляем уровень из EmployeePlan
    if (!employeePlans || employeePlans.length === 0 || !pointSystem) {
      return null;
    }

    const totalPoints = totals.totalPoints || 0;

    // Ищем подходящий EmployeePlan для текущего PointSystem
    const matchingPlan = employeePlans.find((plan) => {
      const from = Number(plan.from) || 0;
      const to = Number(plan.to) || 0;
      return totalPoints >= from && totalPoints <= to;
    });

    if (matchingPlan) {
      return Number(matchingPlan.sort) || null;
    }

    return null;
  }, [listReportData, totals, employeePlans, pointSystem]);

  const handleCompleteMonthConfirm = useCallback(() => {
    if (isMonthClosed) {
      return;
    }
    if (unclosedTasks && unclosedTasks.length > 0) {
      // Открываем модалку с незакрытыми задачами
      if (onOpenUnclosedTasksModal) {
        onOpenUnclosedTasksModal(unclosedTasks);
      }
    } else if (onCompleteMonth) {
      onCompleteMonth();
      onClose();
    }
  }, [isMonthClosed, unclosedTasks, onCompleteMonth, onClose, onOpenUnclosedTasksModal]);

  return (
    <Modal
      open={open}
      closeIcon
      size="large"
      centered={false}
      className={styles.modal}
      onClose={onClose}
    >
      <Modal.Header>{headerTitle}</Modal.Header>
      <Modal.Content className={styles.content}>
        <div className={styles.sections}>
          <div className={styles.section}>
            <div className={styles.topRow}>
              {year && month && <div className={styles.topRowTitle}>Итоги месяца:</div>}
              <div className={styles.topRowRight}>
                <div className={styles.monthHint}>Выберите год и месяц для расчета плана</div>
                <Form className={styles.periodForm}>
                  <Form.Select
                    options={yearOptions}
                    value={year}
                    onChange={(_, { value }) => {
                      setYear(value);
                      // Сохраняем выбранный год в ListReportData, если месяц уже выбран
                      if (value && month && listId && pointSystem) {
                        onListReportDataUpsert({
                          listId,
                          year: value,
                          month,
                        });
                      }
                    }}
                    className={`${styles.yearSelect} ${!year ? styles.selectError : ''}`}
                  />
                  <Form.Select
                    options={monthOptions}
                    value={month}
                    onChange={(_, { value }) => {
                      setMonth(value);
                      // Сохраняем выбранный месяц в ListReportData
                      // При изменении месяца сбрасываются closed = false и closed_at = NULL
                      if (year && value && listId && pointSystem) {
                        onListReportDataUpsert({
                          listId,
                          year,
                          month: value,
                        });
                      }
                    }}
                    className={`${styles.monthSelect} ${!month ? styles.selectError : ''}`}
                  />
                </Form>
              </div>
            </div>

            {!year || !month ? (
              <div className={styles.noDataMessage}>НЕТ ДАННЫХ</div>
            ) : (
              <>
                <div className={styles.categoriesGrid}>
                  {summaryByCategory.map((c) => (
                    <CategorySummaryCard
                      key={c.title}
                      title={c.title}
                      count={c.count}
                      points={c.points}
                      views={c.views}
                    />
                  ))}
                </div>

                <div className={styles.totalsGrid}>
                  <StatRow label="Количество работ" value={totals.workCount} />
                  <StatRow label="Просмотры" value={totals.views} />
                  <StatRow label="Баллы по умолчанию" value={formatPoints(totals.defaultPoints)} />
                  <StatRow label="Дополнительные баллы" value={formatPoints(totals.extraPoints)} />
                  <StatRow label="Баллы по коэффициенту" value={formatPoints(totals.coefficientPoints)} />
                  <StatRow label="Итоги баллов" value={formatPoints(totals.totalPoints)} />
                  {isReportLoading && <div className={styles.loadingHint}>Загрузка...</div>}
                </div>

                <div className={styles.employeeLevelBlock}>
                  <StatRow label="Уровень сотрудника" value={employeeLevel !== null ? employeeLevel : '—'} labelClassName={styles.statLabelSemibold} />
                </div>
              </>
            )}
          </div>

          {year && month && tasksFromOtherLists.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitleCentered}>Задачи в других списках</div>
              <div className={styles.taskBadgesContainer}>
                {tasksFromOtherLists.map((t) => (
                  <TaskBadge key={`other-${t.number}`} task={t} />
                ))}
              </div>
            </div>
          )}

          {year && month && tasksInListClosedInOtherMonth.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitleCentered}>Лишние задачи в списке</div>
              <div className={styles.taskBadgesContainer}>
                {tasksInListClosedInOtherMonth.map((t) => (
                  <TaskBadge key={`other-month-${t.number}`} task={t} />
                ))}
              </div>
            </div>
          )}

          {year && month && unclosedTasksInCurrentList.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitleCentered}>Незакрытые задачи в списке</div>
              <div className={styles.taskBadgesContainer}>
                {unclosedTasksInCurrentList.map((t, idx) => (
                  <TaskNameOnly key={`unclosed-${t.cardId || idx}`} task={t} />
                ))}
              </div>
            </div>
          )}

          {year && month && (
            <div className={styles.section}>
              <div className={styles.sectionTitleCentered}>ДЕТАЛИЗИРОВАННЫЙ ОТЧЕТ ЗА МЕСЯЦ</div>
              <div className={styles.tasks}>
                {detailedTasks.length > 0 ? (
                  detailedTasks.map((t) => (
                    <TaskBlock key={t.number} task={t} />
                  ))
                ) : (
                  <div className={styles.emptyMessage}>
                    {isReportLoading ? 'Загрузка...' : 'Нет данных для отображения'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
});

MonthlyReportModal.propTypes = {
  open: PropTypes.bool,
  /* eslint-disable react/forbid-prop-types */
  pointSystem: PropTypes.object,
  listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  report: PropTypes.object,
  unclosedTasks: PropTypes.array,
  allUnclosedTasksInList: PropTypes.array,
  contentViewPlans: PropTypes.array,
  employeePlans: PropTypes.array,
  /* eslint-enable react/forbid-prop-types */
  isReportLoading: PropTypes.bool.isRequired,
  onReportFetch: PropTypes.func.isRequired,
  onContentViewPlansFetch: PropTypes.func.isRequired,
  onEmployeePlansFetch: PropTypes.func,
  onDampingCoefficientsFetch: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onCompleteMonth: PropTypes.func,
  onOpenUnclosedTasksModal: PropTypes.func,
  onListReportDataUpsert: PropTypes.func,
  onListReportDataFetch: PropTypes.func,
  listReportData: PropTypes.object,
};

MonthlyReportModal.defaultProps = {
  open: true,
  pointSystem: null,
  listId: null,
  report: null,
  unclosedTasks: [],
  allUnclosedTasksInList: [],
  contentViewPlans: [],
  employeePlans: [],
  onEmployeePlansFetch: null,
  onCompleteMonth: null,
  onOpenUnclosedTasksModal: null,
  onListReportDataUpsert: null,
  onListReportDataFetch: null,
  listReportData: null,
};

export default MonthlyReportModal;

